import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudQueue,
  Speed,
  Security,
  Analytics,
  CheckCircle,
  ExpandMore,
  TrendingUp,
  Assessment,
  Storage
} from '@mui/icons-material';

const CloudMigration = () => {
  const benefits = [
    'Migrate applications for enhanced agility and superior user experience',
    'Reduce infrastructure costs and improve scalability',
    'Enhance security and compliance with cloud-native solutions',
    'Enable faster deployment and time-to-market',
    'Improve disaster recovery and business continuity',
    'Access to advanced cloud services and AI capabilities'
  ];

  const services = [
    {
      title: 'Cloud Strategy and Planning',
      description: 'Comprehensive cloud migration strategy and roadmap development',
      features: ['Cloud assessment', 'Migration planning', 'Cost optimization', 'Risk analysis']
    },
    {
      title: 'Application Migration',
      description: 'Seamless migration of applications to cloud platforms',
      features: ['Application analysis', 'Migration execution', 'Testing and validation', 'Go-live support']
    },
    {
      title: 'Data Migration',
      description: 'Secure and efficient migration of data to cloud environments',
      features: ['Data mapping', 'ETL processes', 'Data validation', 'Backup and recovery']
    },
    {
      title: 'Cloud Optimization',
      description: 'Optimize cloud resources and performance post-migration',
      features: ['Performance tuning', 'Cost optimization', 'Monitoring setup', 'Security hardening']
    }
  ];

  const faqs = [
    {
      question: 'What is cloud migration?',
      answer: 'Cloud migration is the process of moving applications, data, and workloads from on-premises infrastructure to cloud platforms. This includes planning, execution, and optimization to ensure successful transition to cloud environments while maintaining performance and security.'
    },
    {
      question: 'How long does cloud migration take?',
      answer: 'Cloud migration timelines vary based on application complexity, data volume, and migration strategy. Simple applications may take 2-4 months, while complex enterprise systems can take 6-18 months. We provide detailed timelines during the assessment phase.'
    },
    {
      question: 'What are the benefits of cloud migration?',
      answer: 'Cloud migration offers cost savings, improved scalability, enhanced security, better performance, faster deployment, and access to advanced cloud services. It also enables remote work capabilities and reduces infrastructure maintenance overhead.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Cloud Migration
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Migrate applications for enhanced agility and superior user experience across cloud platforms
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Why Migrate to the Cloud?
          </Typography>
          
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CloudQueue sx={{ color: '#4caf50', mr: 2, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Cloud Migration Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    <List>
                      {service.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Speed sx={{ color: '#4caf50', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Start Your Cloud Migration Journey
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get a comprehensive cloud migration assessment and roadmap
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request Migration Assessment
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default CloudMigration;
